
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, ExternalLink, MessageSquare, CheckCircle, Eye, Send, RefreshCw } from "lucide-react";
import { FeedbackWithProfile } from "@/types/admin";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface FeedbackManagementSectionProps {
  feedback: FeedbackWithProfile[];
  loading: boolean;
  onFeedbackUpdate?: () => void;
}

export const FeedbackManagementSection = ({ 
  feedback, 
  loading, 
  onFeedbackUpdate 
}: FeedbackManagementSectionProps) => {
  const { user } = useAuth();
  const [responseTexts, setResponseTexts] = useState<{ [key: string]: string }>({});
  const [submittingResponse, setSubmittingResponse] = useState<{ [key: string]: boolean }>({});
  const [resolvingFeedback, setResolvingFeedback] = useState<{ [key: string]: boolean }>({});
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Bug Report":
        return "destructive";
      case "Feature Request":
        return "default";
      case "General":
        return "secondary";
      case "UI/UX":
        return "outline";
      case "Performance":
        return "default";
      default:
        return "secondary";
    }
  };

  const handleResponseSubmit = async (feedbackId: string) => {
    const responseText = responseTexts[feedbackId];
    if (!responseText?.trim()) {
      toast.error("Please enter a response");
      return;
    }

    setSubmittingResponse(prev => ({ ...prev, [feedbackId]: true }));

    try {
      const feedbackItem = feedback.find(f => f.id === feedbackId);
      
      const { error } = await supabase
        .from("feedback")
        .update({
          admin_response: responseText.trim()
        })
        .eq("id", feedbackId);

      if (error) throw error;

      // Send email notification to user about admin response
      if (feedbackItem) {
        try {
          await supabase.functions.invoke('send-notification-email', {
            body: {
              user_id: feedbackItem.user_id,
              email_type: 'feedback_response',
              subject: 'Your feedback received a response! 💬',
              content: {
                title: 'Admin Response to Your Feedback',
                message: `An admin has responded to your feedback titled "${feedbackItem.title}". Check the Feedback Center to see their response.`,
                action_url: `${window.location.origin}/feedback`,
                action_text: 'View Response'
              }
            }
          });
        } catch (emailError) {
          console.error("Failed to send notification email:", emailError);
          // Don't fail the operation for email errors
        }
      }

      toast.success("Response added successfully");
      setResponseTexts(prev => ({ ...prev, [feedbackId]: "" }));
      
      // Refresh data instead of reloading the page
      if (onFeedbackUpdate) {
        onFeedbackUpdate();
      }
    } catch (error) {
      console.error("Error adding response:", error);
      toast.error("Failed to add response");
    } finally {
      setSubmittingResponse(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleMarkResolved = async (feedbackId: string) => {
    setResolvingFeedback(prev => ({ ...prev, [feedbackId]: true }));

    try {
      const { error } = await supabase
        .from("feedback")
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id
        })
        .eq("id", feedbackId);

      if (error) throw error;

      toast.success("Feedback marked as resolved");
      
      // Refresh data instead of reloading the page
      if (onFeedbackUpdate) {
        onFeedbackUpdate();
      }
    } catch (error) {
      console.error("Error resolving feedback:", error);
      toast.error("Failed to resolve feedback");
    } finally {
      setResolvingFeedback(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  // Show only unresolved feedback and limit to 10 items
  const unresolvedFeedback = feedback.filter(item => !item.resolved_at);
  const displayedFeedback = unresolvedFeedback.slice(0, 10);
  const hasMoreFeedback = unresolvedFeedback.length > 10;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-poppins text-2xl">User Feedback</CardTitle>
              <CardDescription className="font-inter">
                Recent unresolved feedback submissions ({unresolvedFeedback.length} total)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {onFeedbackUpdate && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onFeedbackUpdate}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              )}
              <Link to="/admin-feedback">
                <Button variant="outline" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  All Feedback
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
            </div>
          ) : displayedFeedback.length === 0 ? (
            <p className="text-center py-8 text-gray-500 font-inter">
              No unresolved feedback submissions.
            </p>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-poppins">Title</TableHead>
                    <TableHead className="font-poppins">Category</TableHead>
                    <TableHead className="font-poppins">User</TableHead>
                    <TableHead className="font-poppins">Date</TableHead>
                    <TableHead className="font-poppins">Message</TableHead>
                    <TableHead className="font-poppins">Response</TableHead>
                    <TableHead className="font-poppins">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedFeedback.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium font-inter max-w-[150px]">
                        <div className="truncate">{item.title}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getCategoryColor(item.category)}>
                          {item.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-inter">
                        {item.user_name}
                      </TableCell>
                      <TableCell className="font-inter">
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="flex items-center gap-2">
                          <div className="font-inter text-sm text-gray-600 truncate flex-1">
                            {item.message}
                          </div>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setSelectedMessage(item.message)}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>{item.title}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="font-semibold mb-2">Full Message:</h4>
                                  <p className="text-gray-700 font-inter whitespace-pre-wrap bg-gray-50 p-4 rounded max-h-64 overflow-y-auto">
                                    {item.message}
                                  </p>
                                </div>
                                {item.admin_response && (
                                  <div>
                                    <h4 className="font-semibold mb-2">Admin Response:</h4>
                                    <p className="text-gray-700 font-inter whitespace-pre-wrap bg-blue-50 p-4 rounded border-l-4 border-l-blue-500">
                                      {item.admin_response}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        {item.admin_response ? (
                          <div className="text-xs text-gray-600 p-2 bg-green-50 rounded border">
                            Response sent: {item.admin_response.substring(0, 50)}...
                          </div>
                        ) : (
                          <Textarea
                            value={responseTexts[item.id] || ""}
                            onChange={(e) => setResponseTexts(prev => ({ ...prev, [item.id]: e.target.value }))}
                            placeholder="Quick response..."
                            rows={2}
                            className="text-xs"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!item.admin_response && (
                            <Button
                              onClick={() => handleResponseSubmit(item.id)}
                              disabled={submittingResponse[item.id] || !responseTexts[item.id]?.trim()}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 h-7 px-2"
                            >
                              {submittingResponse[item.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                          <Button
                            onClick={() => handleMarkResolved(item.id)}
                            disabled={resolvingFeedback[item.id]}
                            size="sm"
                            variant="outline"
                            className="border-green-600 text-green-600 hover:bg-green-50 h-7 px-2"
                          >
                            {resolvingFeedback[item.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {hasMoreFeedback && (
                <div className="text-center pt-4 border-t">
                  <p className="text-gray-600 font-inter mb-3">
                    Showing 10 of {unresolvedFeedback.length} unresolved feedback items
                  </p>
                  <Link to="/admin-feedback">
                    <Button variant="outline" className="flex items-center gap-2 mx-auto">
                      <ExternalLink className="h-4 w-4" />
                      View All Feedback
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
